<template>
  <div v-if="authorized">
    <KTPortlet :title="title">
      <template v-slot:body>
        <b-row>
          <b-col>
            <b-button
              class="full-width"
              @click="createNewAcquirer"
              type="button"
              variant="primary"
              >Create New Acquirer</b-button
            >
          </b-col>
          <b-col :cols="4">
            <b-input v-model="search" placeholder="Quick Search...." />
          </b-col>
        </b-row>
        <div class="aq-table">
          <v-data-table
            :item-class="rowClass"
            :headers="headers"
            :items="acquirers"
            item-key="acquirerId"
            class="elevation-1"
            :search="search"
            :loading="loading"
          >
            <template v-slot:item.acquirerId="{ item }">
              <b-link @click="updateAcquirer(item)">{{
                item.acquirerId
              }}</b-link>
            </template>

            <template v-slot:item.actions="{ item }">
              <b-button
                type="button"
                variant="danger"
                @click="confirmDelete(item)"
                >Delete</b-button
              >
            </template>

            <template v-slot:loading>
              <b-spinner variant="primary" label="Spinning"></b-spinner>
            </template>
          </v-data-table>
        </div>
      </template>
    </KTPortlet>
    <LoadingModal v-bind="loadingModal" />
    <AlertModal
      v-bind="alertModal"
      v-model="alertModal.show"
      @dismiss="resetAlert"
    />
    <ConfirmationModal
      v-bind="confirmationModal"
      v-model="confirmationModal.show"
      @dismiss="resetConfirmation"
    />
    <AcquirerModal v-bind="aquirerModal" @close="closeAcquireModal" />
  </div>
</template>
<style>
.aq-table {
  margin-top: 1rem;
}
</style>
<script>
import KTPortlet from "@/views/partials/content/Portlet";
import { API, graphqlOperation } from "aws-amplify";
import AlertModalMixin from "@/components/widgets/AlertModalMixin.js";
import AcquirerModal from "@/components/widgets/AcquirerModal.vue";

const AlertModal = AlertModalMixin.AlertModal;
const LoadingModal = AlertModalMixin.LoadingModal;
const ConfirmationModal = AlertModalMixin.ConfirmationModal;

export default {
  mixins: [AlertModalMixin],
  components: {
    KTPortlet,
    AcquirerModal
  },
  data() {
    return {
      acquirers: [],
      stopLoading: false,
      search: "",
      aquirerModal: {
        dialog: false,
        acquirerRecord: null
      }
    };
  },
  computed: {
    title() {
      return `Acquirers`;
    },
    loading() {
      return !this.stopLoading && (!this.acquirers || !this.acquirers.length);
    },
    authorized() {
      return this.$role.verify([this.$role.ADMIN]);
    },
    headers() {
      return [
        {
          text: "ID",
          value: "acquirerId",
          align: "center"
        },
        {
          text: "Name",
          value: "name",
          align: "center"
        },
        { text: "", value: "actions", sortable: false, align: "center" }
      ];
    }
  },
  methods: {
    rowClass() {
      const rowClass = "table-rows";
      return rowClass;
    },
    async getAcquirers() {
      const getAcquirers = `
                query ListAcquirers {
                    listAcquirers {
                        acquirers{
                            acquirerId
                            name
                        }
                    }
                }
              `;

      const getAcquirersResponse = await API.graphql({
        ...graphqlOperation(getAcquirers)
      });

      return getAcquirersResponse.data.listAcquirers.acquirers;
    },
    async confirmDelete(item) {
      const acquirerId = item.acquirerId;
      const name = item.name;

      const confirm = await this.confirm(
        `Delete Acquirer?`,
        `Are you sure you want to delete Acquirer ${acquirerId} - ${name}?`,
        ConfirmationModal.WARNING
      );
      if (!confirm) return;
      let requestMessage = `Successfully deleted ${acquirerId} - ${name}`;
      let alertType = AlertModal.SUCCESS;
      this.startLoadingDialog(`Deleting ${acquirerId} - ${name}`);
      try {
        const result = await this.deleteAcquirer(acquirerId);
        if (!result.success) {
          requestMessage = result.errorMessage;
        } else {
          this.removeAcquireItem(acquirerId);
        }
      } catch (err) {
        this.$logger.error(err);
        requestMessage = `There was an error deleting ${acquirerId} - ${name}`;
        alertType = AlertModal.ERROR;
      }
      this.stopLoadingDialog(requestMessage, alertType);
    },
    async deleteAcquirer(acquirerId) {
      let success = false;
      let errorMessage = "Failed to delete Acquirer: " + acquirerId;
      const deleteAcquirer = `
            mutation DeleteAcquirer($acquirerId: String!) {
                deleteAcquirer(acquirerId: $acquirerId) {
                  success
                  errorMessage
                }
            }
          `;

      try {
        const deleteAcquirerResponse = await API.graphql({
          ...graphqlOperation(deleteAcquirer, { acquirerId })
        });

        success = deleteAcquirerResponse.data.deleteAcquirer.success;
        errorMessage = deleteAcquirerResponse.data.deleteAcquirer.errorMessage;
      } catch (e) {
        this.$logger.error("Error deleting acquirer");
        this.$logger.error(e);
      }
      return { success, errorMessage };
    },
    removeAcquireItem(acquirerId) {
      const index = this.acquirers.findIndex(a => a.acquirerId === acquirerId);
      this.acquirers.splice(index, 1);
    },
    createNewAcquirer() {
      this.openAcquireModal();
    },
    updateAcquirer(item) {
      this.openAcquireModal(item);
    },
    openAcquireModal(item) {
      this.aquirerModal.acquirerRecord = item ? Object.assign({}, item) : null;
      this.aquirerModal.dialog = true;
    },
    closeAcquireModal(savedItem) {
      if (!!savedItem) {
        this.updateAcquirerItem(savedItem);
      }
      this.aquirerModal.dialog = false;
      this.$nextTick(() => {
        this.aquirerModal.acquirerRecord = null;
      });
    },
    updateAcquirerItem(item) {
      const index = this.acquirers.findIndex(
        a => a.acquirerId == item.acquirerId
      );
      if (index > -1) this.acquirers[index].name = item.name;
      else
        this.acquirers.push({ acquirerId: item.acquirerId, name: item.name });
    }
  },
  created: async function() {
    try {
      this.acquirers = await this.getAcquirers();
    } catch (error) {
      this.stopLoading = true;
      this.$logger.error(error);
      this.alert(`Unable to retrieve acquirers.`, AlertModal.ERROR);
    }
  }
};
</script>

<style lang="scss" scoped></style>
